import React, { createContext, useContext, useState, useEffect } from 'react';
import { AuthContextType, AuthState, LoginCredentials, User } from '../types/auth';
import { api, postData } from '../services/api';
import { PATH_SEGMENTS } from '../config/paths';

// Mock user data
const mockUser: User = {
  id: "1",
  email: "guest@vhu.ai",
  username: "guest",
  first_name: "Guest",
  last_name: "User",
  avatar: "GU"
};

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  isLoading: true,
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>(initialState);
  const hasInitializedRef = React.useRef(false);

  useEffect(() => {
    // Prevent duplicate calls from React StrictMode
    if (hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;

    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Verify token and get user data
          const response = await fetch(api(PATH_SEGMENTS.USER, 'me'), {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const userData = await response.json();
            setState({
              user: userData,
              token,
              isAuthenticated: true,
              isLoading: false,
            });
          } else {
            // Token is invalid
            localStorage.removeItem('token');
            setState({
              ...initialState,
              isLoading: false,
            });
          }
        } catch (error) {
          console.error('Auth initialization error:', error);
          localStorage.removeItem('token');
          setState({
            ...initialState,
            isLoading: false,
          });
        }
      } else {
        setState({
          ...initialState,
          isLoading: false,
        });
      }
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      // Format the request to match FastAPI's OAuth2PasswordRequestForm
      const formData = new URLSearchParams();
      formData.append('username', credentials.email);
      formData.append('password', credentials.password);

      const response = await postData<{ access_token: string; user: User }>(
        api(PATH_SEGMENTS.AUTH, PATH_SEGMENTS.LOGIN),
        formData.toString(),
        'POST',
        'application/x-www-form-urlencoded'
      );

      const { access_token: token, user } = response;
      localStorage.setItem('token', token);
      
      setState({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint if needed
      await postData(api(PATH_SEGMENTS.AUTH, PATH_SEGMENTS.LOGOUT), {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      setState({
        ...initialState,
        isLoading: false,
      });
    }
  };

  const updateUser = (user: User) => {
    setState(prev => ({
      ...prev,
      user,
    }));
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 